import { useEffect, useRef } from 'react';
import {
  RiCompass3Line,
  RiFileTextLine,
  RiGridLine,
  RiLightbulbFlashLine,
  RiNodeTree,
  RiShape2Line,
  RiSparkling2Line,
} from 'react-icons/ri';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { DossierPanel } from './DossierPanel';
import { AssessmentPanel } from './AssessmentPanel';
import { ElementInspector } from './ElementInspector';
import { OperatePanel } from './OperatePanel';
import { GraphPanel } from './GraphPanel';
import { AchPanel } from './AchPanel';
import { GeoPanel } from './GeoPanel';
import { useCanvasStore } from '../canvas/canvasStore';
import { useStore } from '../state/store';

type TabId = 'dossier' | 'assess' | 'graph' | 'ach' | 'geo' | 'operate' | 'element';

export function RightPanel() {
  const tab = useStore(state => state.rightTab) as TabId;
  const setRightTab = useStore(state => state.setRightTab);
  const controller = useCanvasStore(state => state.controller);
  const revision = useCanvasStore(state => state.revision);
  const activeTargetId = useStore(state => state.activeTargetId);
  const aiAnalysis = useStore(state => state.aiAnalysis);

  const prevSelection = useRef(0);
  const selectionSize = controller?.selection.size ?? 0;

  // 在关联墙中选中元素时自动切到「元素」页
  useEffect(() => {
    if (selectionSize > 0 && prevSelection.current === 0) setRightTab('element');
    prevSelection.current = selectionSize;
  }, [selectionSize, revision, setRightTab]);

  // 切换对象且当前处于「元素」页时回到「档案」页
  useEffect(() => {
    if (activeTargetId && useStore.getState().rightTab === 'element') setRightTab('dossier');
  }, [activeTargetId, setRightTab]);

  return (
    <aside className="app-panel flex min-h-0 w-[400px] shrink-0 flex-col border-l border-border">
      <Tabs value={tab} onValueChange={value => setRightTab(value as TabId)} className="flex min-h-0 flex-1 flex-col">
        <TabsList className="shrink-0 gap-0 overflow-x-auto">
          <TabsTrigger value="dossier" className="gap-1 shrink-0">
            <RiFileTextLine className="size-3.5" />
            档案
          </TabsTrigger>
          <TabsTrigger value="assess" className="gap-1 shrink-0">
            <RiLightbulbFlashLine className="size-3.5" />
            研判
            {aiAnalysis ? <span className="size-1.5 rounded-full bg-[var(--color-assess)]" /> : null}
          </TabsTrigger>
          <TabsTrigger value="ach" className="gap-1 shrink-0">
            <RiGridLine className="size-3.5" />
            矩阵
          </TabsTrigger>
          <TabsTrigger value="geo" className="gap-1 shrink-0">
            <RiCompass3Line className="size-3.5" />
            地理
          </TabsTrigger>
          <TabsTrigger value="graph" className="gap-1 shrink-0">
            <RiNodeTree className="size-3.5" />
            图谱
          </TabsTrigger>
          <TabsTrigger value="operate" className="gap-1 shrink-0">
            <RiSparkling2Line className="size-3.5" />
            操作
          </TabsTrigger>
          <TabsTrigger value="element" className="gap-1 shrink-0">
            <RiShape2Line className="size-3.5" />
            元素
            {selectionSize > 0 ? (
              <Badge variant="default" className="px-1 py-0 font-mono text-[9px]">
                {selectionSize}
              </Badge>
            ) : null}
          </TabsTrigger>
        </TabsList>

        <ScrollArea className="min-h-0 flex-1">
          {tab === 'dossier' && <DossierPanel />}
          {tab === 'assess' && <AssessmentPanel />}
          {tab === 'graph' && <GraphPanel />}
          {tab === 'ach' && <AchPanel />}
          {tab === 'geo' && <GeoPanel />}
          {tab === 'operate' && <OperatePanel />}
          {tab === 'element' && <ElementInspector />}
        </ScrollArea>
      </Tabs>
    </aside>
  );
}